/**
 * Admin > Audit Log — read-only viewer over audit.audit_log (RBAC hardening
 * Phase 1). Shows who did what: destructive ops, money movement triggers,
 * provisioning, impersonation. The BFF endpoint is global-Admin gated; rows
 * are written by auditLog() at the same choke points that enforce
 * authorization, so this page reflects enforcement, not intent.
 *
 * Filtering happens client-side within the loaded window (latest 200 rows by
 * default, "Load more" widens to 500).
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Body1,
  Button,
  Caption1,
  Dropdown,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Title2,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { ArrowClockwiseRegular, SearchRegular, ShieldTaskRegular } from '@fluentui/react-icons';
import { bffFetch } from '@shared/api';
import { veracityColors } from '@shared/ui';

interface AuditRow {
  id: number;
  at: string;
  actor_oid: string;
  actor_name: string;
  impersonated_oid: string | null;
  action: string;
  module: string;
  target_kind: string;
  target_id: string;
  target_label: string;
  details: Record<string, unknown>;
}

interface AuditResponse {
  configured: boolean;
  rows: AuditRow[];
}

const useStyles = makeStyles({
  page: {
    padding: tokens.spacingVerticalXXL,
    maxWidth: '1100px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalS,
  },
  titleIcon: {
    fontSize: '28px',
    color: veracityColors.veracityOrange,
  },
  subtitle: {
    color: veracityColors.duskBlue,
    marginBottom: tokens.spacingVerticalXL,
    display: 'block',
  },
  filters: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  searchBox: {
    minWidth: '260px',
  },
  table: {
    border: `1px solid ${veracityColors.firstLight}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: '#FFFFFF',
    overflowX: 'auto',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '150px 160px 190px 1fr 90px',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${veracityColors.firstLight}`,
    alignItems: 'baseline',
    ':last-child': { borderBottom: 'none' },
  },
  headRow: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
    color: veracityColors.duskBlue,
    backgroundColor: veracityColors.firstLight,
  },
  cellTime: {
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.duskBlue,
    whiteSpace: 'nowrap',
  },
  cellActor: {
    fontSize: tokens.fontSizeBase300,
    color: veracityColors.midnightCharcoal,
    overflowWrap: 'anywhere',
  },
  cellAction: {
    fontFamily: 'monospace',
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.midnightCharcoal,
  },
  cellTarget: {
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.midnightCharcoal,
    overflowWrap: 'anywhere',
  },
  cellModule: {
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.duskBlue,
  },
  detailsText: {
    color: veracityColors.duskBlue,
    display: 'block',
  },
  impersonationTag: {
    color: veracityColors.veracityOrange,
    fontWeight: tokens.fontWeightSemibold,
    marginLeft: tokens.spacingHorizontalXS,
  },
  statusBar: {
    marginTop: tokens.spacingVerticalM,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    color: veracityColors.duskBlue,
  },
  empty: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: veracityColors.duskBlue,
  },
});

const ALL = '(all)';

export function AuditLogPage() {
  const styles = useStyles();
  const [limit, setLimit] = useState(200);
  const [query, setQuery] = useState('');
  const [moduleFilter, setModuleFilter] = useState(ALL);
  const [actionFilter, setActionFilter] = useState(ALL);

  const auditQuery = useQuery({
    queryKey: ['admin', 'auditLog', limit],
    queryFn: async (): Promise<AuditResponse> => {
      const res = await bffFetch(`/api/admin/audit-log?limit=${limit}`);
      if (!res.ok) throw new Error(`Audit log fetch failed (${res.status})`);
      return res.json();
    },
    staleTime: 30_000,
  });

  const rows = useMemo(() => auditQuery.data?.rows ?? [], [auditQuery.data]);
  const modules = useMemo(
    () => Array.from(new Set(rows.map((r) => r.module).filter(Boolean))).sort(),
    [rows],
  );
  const actions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.action).filter(Boolean))).sort(),
    [rows],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (moduleFilter !== ALL && r.module !== moduleFilter) return false;
      if (actionFilter !== ALL && r.action !== actionFilter) return false;
      if (!q) return true;
      return (
        r.actor_name.toLowerCase().includes(q) ||
        r.action.toLowerCase().includes(q) ||
        r.target_label.toLowerCase().includes(q) ||
        r.target_id.toLowerCase().includes(q)
      );
    });
  }, [rows, query, moduleFilter, actionFilter]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <ShieldTaskRegular className={styles.titleIcon} />
        <Title2>Audit Log</Title2>
      </div>
      <Caption1 className={styles.subtitle}>
        Privileged and destructive actions recorded by the BFF at the moment of enforcement:
        deletes, sync triggers, provisioning, release, and impersonation. Append-only.
      </Caption1>

      {auditQuery.isLoading && <Spinner label="Loading audit log..." />}

      {auditQuery.isError && (
        <MessageBar intent="error">
          <MessageBarBody>
            Could not load the audit log. {auditQuery.error instanceof Error ? auditQuery.error.message : ''}
          </MessageBarBody>
        </MessageBar>
      )}

      {auditQuery.data && !auditQuery.data.configured && (
        <MessageBar intent="info">
          <MessageBarBody>
            The audit database is not provisioned yet, so entries are currently echoed to the
            BFF console only. To activate: create the audit Postgres app role, set
            AUDIT_PG_CONNECTION_STRING (dev) or the vera-audit-pg-connection-string Key Vault
            secret (prod), then run <code>npm run audit:migrate -w @vera/bff</code>.
          </MessageBarBody>
        </MessageBar>
      )}

      {auditQuery.data?.configured && (
        <>
          <div className={styles.filters}>
            <Input
              className={styles.searchBox}
              placeholder="Filter by actor, action, or target…"
              contentBefore={<SearchRegular />}
              value={query}
              onChange={(_, data) => setQuery(data.value)}
            />
            <Dropdown
              value={moduleFilter}
              selectedOptions={[moduleFilter]}
              onOptionSelect={(_, data) => setModuleFilter(data.optionValue ?? ALL)}
            >
              <Option value={ALL}>{ALL}</Option>
              {modules.map((m) => (
                <Option key={m} value={m}>{m}</Option>
              ))}
            </Dropdown>
            <Dropdown
              value={actionFilter}
              selectedOptions={[actionFilter]}
              onOptionSelect={(_, data) => setActionFilter(data.optionValue ?? ALL)}
            >
              <Option value={ALL}>{ALL}</Option>
              {actions.map((a) => (
                <Option key={a} value={a}>{a}</Option>
              ))}
            </Dropdown>
            <Button
              icon={<ArrowClockwiseRegular />}
              onClick={() => auditQuery.refetch()}
              disabled={auditQuery.isFetching}
            >
              Refresh
            </Button>
          </div>

          <div className={styles.table}>
            <div className={mergeClasses(styles.row, styles.headRow)}>
              <span>Time</span>
              <span>Actor</span>
              <span>Action</span>
              <span>Target</span>
              <span>Module</span>
            </div>
            {visible.length === 0 && (
              <div className={styles.empty}>
                <Body1>
                  {rows.length === 0
                    ? 'No audit entries yet. They appear as privileged actions happen.'
                    : 'No entries match the current filters.'}
                </Body1>
              </div>
            )}
            {visible.map((r) => {
              const detailsStr =
                r.details && Object.keys(r.details).length > 0 ? JSON.stringify(r.details) : '';
              return (
                <div key={r.id} className={styles.row}>
                  <span className={styles.cellTime}>{new Date(r.at).toLocaleString()}</span>
                  <span className={styles.cellActor}>
                    {r.actor_name || r.actor_oid}
                    {r.impersonated_oid && (
                      <Tooltip
                        content={`Acting while impersonating ${r.impersonated_oid}`}
                        relationship="label"
                      >
                        <span className={styles.impersonationTag}>(impersonating)</span>
                      </Tooltip>
                    )}
                  </span>
                  <span className={styles.cellAction}>{r.action}</span>
                  <span className={styles.cellTarget}>
                    {r.target_label || r.target_id || '(none)'}
                    {detailsStr && (
                      <Caption1 className={styles.detailsText} title={detailsStr}>
                        {detailsStr.length > 120 ? `${detailsStr.slice(0, 120)}…` : detailsStr}
                      </Caption1>
                    )}
                  </span>
                  <span className={styles.cellModule}>{r.module}</span>
                </div>
              );
            })}
          </div>

          <div className={styles.statusBar}>
            <Caption1>
              Showing {visible.length} of {rows.length} loaded entries
              {rows.length >= limit ? ` (latest ${limit})` : ''}
            </Caption1>
            {rows.length >= limit && limit < 500 && (
              <Button size="small" onClick={() => setLimit(500)}>
                Load more (up to 500)
              </Button>
            )}
            {auditQuery.isFetching && <Spinner size="tiny" />}
          </div>
        </>
      )}
    </div>
  );
}

export default AuditLogPage;
